/**
 =========================================================
 * Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState, useRef } from "react";

// react-router-dom components
import { useLocation, NavLink, Link as RouterLink, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import { Stack, Icon, Link, Divider, List, Box, Popover, Card, Typography } from "@mui/material";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 PRO React examples
import SidenavCollapse from "utils/Sidenav/SidenavCollapse";
import SidenavList from "utils/Sidenav/SidenavList";
import SidenavItem from "utils/Sidenav/SidenavItem";
import NotificationItem from "utils/Items/NotificationItem";
import { generateColorFromString } from "utils/GenerateColorFromString/GenerateColorFromString";

// Custom styles for the Sidenav
import SidenavRoot from "utils/Sidenav/SidenavRoot";
// import sidenavLogoLabel from "utils/Sidenav/styles/sidenav";

import { useSelector, useDispatch } from "react-redux";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { enqueueSnackbar } from "notistack";

import {
  GET_USER_WORKSPACES,
  USER_TYPES,
  DECLINE_INVITE_USER_TO_WORKSPACE_REQUEST_URL,
} from "lib/constants";
import { getAPICall, postAPICall } from "lib/callApi";
import { hubAction } from "redux/slice/hub";
import { homeAction } from "redux/slice/home";
import useAuth from "auth/useAuth";

// Material Dashboard 2 PRO React context
import { useMaterialUIController, setMiniSidenav } from "context";
import sampleProfileImage from "assets/images/sample.png";

import NotificationsMenu from "utils/NotificationMenu";
import AlysIcon from "./icons/AlysIcon";
import "styles/sidenav.scss";
import UserInvitationNotificationItem from "./UserInvitationNotificationItem";

const componentMap = {
  AlysIcon,
};

function Sidenav({ color, brand, brandName, routes, bottomRoutes, ...rest }) {
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false);
  const dispatch = useDispatch();
  const [controller, muiDispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const { handleLogout, handleWorkspaceSwitch } = useAuth();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(0)[1];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];
  const theme = useTheme();
  const isMobileDisplay = useMediaQuery(theme.breakpoints.only("xs"));
  const navigate = useNavigate();

  // Settings and notification popover
  const user = useSelector((state) => state.loginUser.loginUserState);
  const appSettings = useSelector((state) => state.appSettings);
  const { notifications: hubNotifications } = useSelector((state) => state.hub);
  const { notifications, loading: notificationsLoading } = useSelector(
    (state) => state.home.notifications
  );

  const roleName = user?.UserCompaniesLinks?.[0]?.UserCompanyRole?.role_name ?? null;

  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverContent, setPopoverContent] = useState(null);
  const handlePopoverClose = () => {
    setPopoverAnchor(null);
  };

  const [userFontSize, setUserFontSize] = useState(14);
  const [roleFontSize, setRoleFontSize] = useState(10);
  const userContainerRef = useRef(null);
  const roleContainerRef = useRef(null);
  const minFontSize = 6;

  const adjustFontSize = (container, maxFontSize) => {
    let currentFontSize = maxFontSize;
    container.style.fontSize = `${currentFontSize}px`;

    while (container.scrollWidth > container.clientWidth && currentFontSize > minFontSize) {
      currentFontSize -= 1;
      container.style.fontSize = `${currentFontSize}px`;
    }

    return currentFontSize;
  };

  useEffect(() => {
    const userContainer = userContainerRef.current;
    if (userContainer) {
      setUserFontSize(adjustFontSize(userContainer, 14));
    }

    const roleContainer = roleContainerRef.current;
    if (roleContainer) {
      setRoleFontSize(adjustFontSize(roleContainer, 10));
    }
  }, [user]);

  const [userWorkspaces, setUserWorkspaces] = useState([]);
  const [showExpandedWorkspaces, setShowExpandedWorkspaces] = useState(false);
  const [activeWorkspace, setActiveWorkspace] = useState({});
  const [inviteLoading, setInviteLoading] = useState(false);

  const fetchUserWorkspaces = async () => {
    const result = await getAPICall(GET_USER_WORKSPACES, true);

    if (result?.data) {
      setUserWorkspaces(result?.data || []);
      setActiveWorkspace(result?.data?.find?.((workspace) => workspace.currentlyActive) || {});
    }
  };

  useEffect(() => {
    if (user.type !== USER_TYPES.ADMIN) fetchUserWorkspaces();
  }, []);

  const callLogout = () => {
    handleLogout();
  };

  const fetchNotifications = async () => {
    if (!notificationsLoading) {
      dispatch(homeAction.fetchNotificationsSaga());
    }
  };

  const handleDeclineInvite = async (inviteCode) => {
    setInviteLoading(true);
    const response = await postAPICall(DECLINE_INVITE_USER_TO_WORKSPACE_REQUEST_URL, true, {
      inviteCode,
    });

    if (response?.data?.success) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "success",
          title: "Success",
          text: "You have declined the invitation!",
        }),
        {
          variant: "custom",
        }
      );

      fetchNotifications();
    } else {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: response.data.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }

    setInviteLoading(false);
    if (response?.data?.success) {
      fetchUserWorkspaces();
      handlePopoverClose();
    }
  };

  const handleAcceptInvite = async (inviteCode) => {
    enqueueSnackbar(
      JSON.stringify({
        icon: "success",
        title: "Success",
        text: "You have accepted the invitation!",
      }),
      {
        variant: "custom",
      }
    );
    navigate(`/workspace?redirect=invited-user&invite_code=${inviteCode}`);
  };

  // Render the profile menu
  const renderProfileMenu = () => (
    <Card
      sx={{
        borderRadius: 0,
        p: 1,
      }}
    >
      <List>
        <Box id="notifications-menu-span-wrapper">
          <NotificationItem
            icon={<Icon fontSize="small">person</Icon>}
            title="Profile Settings"
            onClick={() => {
              handlePopoverClose();
              navigate("/profile-settings");
            }}
          />
          <NotificationItem
            icon={<Icon fontSize="small">emoji_events</Icon>}
            title="Trophy Cabinet"
            onClick={() => {
              handlePopoverClose();
              navigate("/trophy-cabinet");
            }}
          />
          <NotificationItem
            icon={<Icon fontSize="small">power_settings_new_icon</Icon>}
            title="Logout"
            onClick={() => {
              handlePopoverClose();
              callLogout();
            }}
          />
          {(activeWorkspace?.logo || activeWorkspace.companyLogo) && (
            <NotificationItem
              icon={
                <MDBox
                  bgColor={generateColorFromString(activeWorkspace.workspaceName)}
                  color="white"
                  sx={{
                    height: "20px",
                    width: "20px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {activeWorkspace.companyLogo ? (
                    <img src={activeWorkspace.companyLogo} alt="logo" height="100%" width="100%" />
                  ) : (
                    activeWorkspace.logo
                  )}
                </MDBox>
              }
              title="Workspaces"
              onClick={() => setShowExpandedWorkspaces((prev) => !prev)}
            />
          )}
          {showExpandedWorkspaces &&
            userWorkspaces.map((userWorkspace) => (
              <>
                <NotificationItem
                  icon={
                    <MDBox
                      key={userWorkspace.workspaceId}
                      bgColor={generateColorFromString(userWorkspace.workspaceName)}
                      color="white"
                      sx={{
                        height: "20px",
                        width: "20px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {userWorkspace.companyLogo ? (
                        <img
                          src={userWorkspace.companyLogo}
                          alt="logo"
                          height="100%"
                          width="100%"
                        />
                      ) : (
                        userWorkspace.logo
                      )}
                    </MDBox>
                  }
                  title={
                    <MDBox>
                      <MDBox>
                        {userWorkspace.workspaceName}
                        {userWorkspace.currentlyActive && (
                          <Icon fontSize="small" sx={{ color: "#03bafc" }}>
                            check
                          </Icon>
                        )}
                      </MDBox>
                    </MDBox>
                  }
                  disabled={userWorkspace.disabled}
                  onClick={() =>
                    !userWorkspace.disabled && handleWorkspaceSwitch(userWorkspace.workspaceId)
                  }
                />
                {userWorkspace.inviteText ? (
                  <UserInvitationNotificationItem
                    key={userWorkspace.invite_code}
                    title={userWorkspace.inviteText}
                    inviteCode={userWorkspace.invite_code}
                    isLoading={inviteLoading}
                    handleDeclineInvitation={handleDeclineInvite}
                    handleAcceptInvitation={handleAcceptInvite}
                  />
                ) : null}
              </>
            ))}
        </Box>
      </List>
    </Card>
  );

  useEffect(() => {
    if (showExpandedWorkspaces) {
      setPopoverContent(renderProfileMenu());
    }
  }, [showExpandedWorkspaces]);

  const seenNotification = async (id) => {
    dispatch(homeAction.setSeenNotificationSaga(id));
  };

  const clearAllNotifications = async () => {
    dispatch(homeAction.clearNotificationsSaga());
  };

  // render the settings menu dropdown
  const renderSettingsMenu = () => (
    <Card
      sx={{
        borderRadius: 0,
        p: 1,
      }}
    >
      <List>
        <Box id="settings-menu-span-wrapper">
          {appSettings.settingsDropdownItems.map((dropdownItem) => (
            <NotificationItem
              key={dropdownItem.key}
              icon={<Icon fontSize="small">{dropdownItem.icon}</Icon>}
              title={dropdownItem.label}
              onClick={() => {
                handlePopoverClose();
                navigate(dropdownItem.href);
              }}
            />
          ))}
        </Box>
      </List>
    </Card>
  );

  // render notifications menu
  const renderNotificationsMenu = () => (
    <NotificationsMenu
      notifications={notifications}
      inviteLoading={inviteLoading}
      handleDeclineInvite={handleDeclineInvite}
      handleAcceptInvite={handleAcceptInvite}
      handlePopoverClose={handlePopoverClose}
      seenNotification={seenNotification}
      navigate={navigate}
      clearAllNotifications={clearAllNotifications}
    />
  );

  const handlePopoverOpen = (event, key) => {
    if (key === "settings") {
      setPopoverContent(renderSettingsMenu());
    } else if (key === "notifications") {
      if (!notifications.length) {
        return;
      }
      setPopoverContent(renderNotificationsMenu());
    } else if (key === "profile") {
      setPopoverContent(renderProfileMenu());
    }
    setPopoverAnchor(event.currentTarget);
  };

  useEffect(() => {
    if (!notifications.length && popoverAnchor) {
      handlePopoverClose();
    } else if (popoverAnchor) {
      setPopoverContent(renderNotificationsMenu());
    }
  }, [notifications]);

  const popoverOpen = Boolean(popoverAnchor);

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
    fetchNotifications();
    if (user.type !== USER_TYPES.ADMIN) dispatch(hubAction.fetchNotificationsSaga());
  }, []);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(muiDispatch, window.innerWidth < 576);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [muiDispatch, location]);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse) => {
    const template = collapse.map(({ name, route, key, href }) =>
      href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavItem name={name} nested />
        </Link>
      ) : (
        <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
          <SidenavItem name={name} active={route === pathname} nested />
        </NavLink>
      )
    );

    return template;
  };
  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses) =>
    collapses.map(({ name, collapse, route, href, key }) => {
      let returnValue;

      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key + route}
            color={color}
            name={name}
            active={key === itemParentName ? "isParent" : false}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }) =>
              openNestedCollapse === key && currentTarget.classList.contains("MuiListItem-root")
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key + route}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem color={color} name={name} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
            <SidenavItem color={color} name={name} active={key === itemName} />
          </NavLink>
        );
      }
      return <SidenavList key={key + route}>{returnValue}</SidenavList>;
    });

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = (r) =>
    r.map(({ type, name, icon, iconName, title, collapse, noCollapse, key, href, route }) => {
      let returnValue;
      if (isMobileDisplay && key === "performance") return returnValue;

      // Custom icons with the ability to pass in props
      if (typeof icon === "string" && icon === "custom" && iconName) {
        const Component = componentMap[iconName];
        icon = <Component active={key === collapseName} />;
      }

      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key + route}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key + route}>
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          returnValue = (
            <SidenavCollapse
              key={key + route}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() => (openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key))}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key + route}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key + route}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      } else if (type === "nocollapase") {
        returnValue = (
          <Link
            component={RouterLink}
            to={route}
            key={key + route}
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key.toLowerCase() === collapseName.toLowerCase()}
              open={openCollapse === key}
              onClick={() => (openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key))}
              hubNotifications={key === "hub" ? hubNotifications : null}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          </Link>
        );
      } else if (type === "disabled") {
        returnValue = (
          <SidenavCollapse
            key={key + route}
            name={name}
            icon={icon}
            active={key.toLowerCase() === collapseName.toLowerCase()}
            disabled="disabled"
          >
            {collapse ? renderCollapse(collapse) : null}
          </SidenavCollapse>
        );
      } else if (type === "button") {
        returnValue = (
          <SidenavCollapse
            key={key}
            name={name}
            icon={icon}
            onClick={(e) => handlePopoverOpen(e, key)}
            notifications={key === "notifications" ? notifications.length : null}
          />
        );
      }

      return returnValue;
    });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
        <Box sx={{ flex: "0 0 auto" }}>
          <MDBox pt={2} pb={1} px={1.25} textAlign="center">
            {isMobileDisplay && (
              <MDBox
                display={{ xs: "block", xl: "none" }}
                position="absolute"
                top={0}
                right={0}
                pr={1}
                pt={0.25}
                onClick={closeSidenav}
                sx={{ cursor: "pointer" }}
              >
                <MDTypography variant="h6" color="secondary">
                  <Icon sx={{ fontWeight: "bold" }}>close</Icon>
                </MDTypography>
              </MDBox>
            )}
            <MDBox
              component={NavLink}
              to={user.type === USER_TYPES.ADMIN ? "/registrations" : "/home"}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                display: miniSidenav ? "inline-block" : "",
              }}
            >
              {brand && <MDBox component="img" src={brand} alt="Brand" width="100%" />}
            </MDBox>
          </MDBox>
          <Divider
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
            sx={{
              margin: "5px 0",
            }}
          />
          <Box display="flex" alignItems="center" justifyContent="center" width="100%" p="0 0.5rem">
            <Stack direction="column" alignItems="center" spacing={0.5} mx={0.5} width="100%">
              <MDAvatar
                variant="circular"
                sx={{
                  border: `2px solid ${theme.palette.sidebarTextLight.main}`,
                  width: 56,
                  height: 56,
                  "& :hover": {
                    cursor: "pointer",
                  },
                }}
                src={user?.profilePicture || sampleProfileImage}
                onClick={(e) => handlePopoverOpen(e, "profile")}
              />
              <Box
                sx={{
                  width: "100%",
                  textAlign: "center",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                <Typography
                  ref={userContainerRef}
                  sx={{
                    color: theme.palette.sidebarTextLight.main,
                    fontWeight: theme.typography.fontWeightBold,
                    width: "100%",
                    textAlign: "center",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    fontSize: `${userFontSize}px`,
                  }}
                >
                  {user.name}
                </Typography>
                {roleName && (
                  <Typography
                    ref={roleContainerRef}
                    sx={{
                      color: "#8a8a94",
                      fontWeight: theme.typography.fontWeightBold,
                      width: "100%",
                      textAlign: "center",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      fontSize: `${roleFontSize}px`,
                    }}
                  >
                    {roleName}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
          <Divider
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
            sx={{
              margin: "5px 0",
            }}
          />
        </Box>
        <Box sx={{ flex: "1 1 auto", overflow: "auto" }}>
          <List>{renderRoutes(routes)}</List>
        </Box>
        {bottomRoutes && bottomRoutes.length ? (
          <Box sx={{ flex: "0 0 auto" }}>
            <Box mb={1.5}>
              <Divider
                light={
                  (!darkMode && !whiteSidenav && !transparentSidenav) ||
                  (darkMode && !transparentSidenav && whiteSidenav)
                }
                sx={{
                  margin: "5px 0",
                }}
              />
              <List>{renderRoutes(bottomRoutes)}</List>
            </Box>
          </Box>
        ) : null}
      </Box>
      {popoverOpen && (
        <Popover
          open={popoverOpen}
          anchorEl={popoverAnchor}
          onClose={handlePopoverClose}
          sx={{
            "& .MuiPopover-paper": {
              p: 0,
              borderRadius: "0.5rem",
            },
            maxHeight: "400px",
          }}
          anchorOrigin={{
            vertical: "center",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
        >
          {popoverContent}
        </Popover>
      )}
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  // brandName: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
